import React, { useState, useMemo, useRef } from 'react';
import { useCategories } from '../contexts/CategoryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { stripCategoryPrefix } from '../utils/categoryKeywords';
import '../styles/CategoryManagement.css';
import '../styles/DynamicPanel.css';

const CategoryManagement = ({ isOpen, onClose, bookmarks = [], onRenameCategory }) => {
  const { categories, addCategory, removeCategory, updateCategory, getCategoryStats } = useCategories();
  const { t } = useLanguage();
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Memoize category stats to ensure they update when bookmarks change
  const categoryStats = useMemo(() => {
    const stats = categories.reduce((acc, category) => {
      const count = getCategoryStats(category, bookmarks);
      acc[category] = count;
      return acc;
    }, {});
    return stats;
  }, [categories, bookmarks]);

  // Apply search filter (gleiche Logik wie bei der Rezepteverwaltung)
  const filteredCategories = categories.filter(category =>
    stripCategoryPrefix(category).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  // Legt die aktuelle Sucheingabe als neue Kategorie an (Suchfeld = Hinzufügen-Feld, siehe unten)
  const handleAddCategory = async () => {
    if (!searchTerm.trim()) {
      setError(t('categories.emptyError') || 'Kategoriename darf nicht leer sein');
      return;
    }

    // addCategory() ist async (Backend-Call) - ohne await war "success" immer
    // das Promise-Objekt selbst (truthy), ein echtes Scheitern (z.B. 409 weil
    // der Name serverseitig schon existiert) wurde dadurch nie erkannt.
    const success = await addCategory(searchTerm.trim());
    if (success) {
      setSearchTerm('');
      setError('');
      inputRef.current?.focus();
    } else {
      setError(t('categories.existsError') || 'Kategorie existiert bereits');
    }
  };

  const handleDeleteCategory = (category) => {
    const categoryName = typeof category === 'string' ? category : category.name;
    if (window.confirm(t('categories.deleteConfirm', { name: categoryName }) || `Kategorie "${categoryName}" wirklich löschen?`)) {
      removeCategory(categoryName);
    }
  };

  const startEditing = (category) => {
    const categoryName = typeof category === 'string' ? category : category.name;
    setEditingCategory(categoryName);
    setEditingName(categoryName);
    setError('');
  };

  const saveEdit = async () => {
    // Verhindert doppelte Ausführung: Enter im Eingabefeld löst saveEdit()
    // aus und danach onBlur (Fokusverlust) nochmal - ohne diese Sperre würde
    // während des ersten (jetzt echten async) Umbenennens ein zweiter,
    // überflüssiger Rename-Versuch mit denselben (bereits veralteten) Werten
    // starten.
    if (isSaving) return;

    if (!editingName.trim()) {
      setError(t('categories.emptyError') || 'Kategoriename darf nicht leer sein');
      return;
    }

    const oldName = editingCategory;
    const newName = editingName.trim();

    if (oldName === newName) {
      cancelEditing();
      return;
    }

    setIsSaving(true);
    try {
      // updateCategory() ist async (Backend-Call) - ohne await war "success"
      // immer das Promise-Objekt selbst (truthy), wodurch ein Scheitern nie
      // erkannt und obendrein onRenameCategory() ggf. für eine gar nicht
      // umbenannte Kategorie ausgeführt worden wäre.
      const success = await updateCategory(oldName, newName);
      if (success) {
        setEditingCategory(null);
        setEditingName('');
        setError('');

        // Kategorien-Rename wird intern als "neu anlegen + alte löschen"
        // nachgebildet (kein PUT/PATCH-Endpoint für Kategorien im Backend).
        // Lesezeichen speichern ihre Kategorie als Namens-String, würden
        // also sonst an der gelöschten alten Kategorie hängen bleiben und
        // aus der UI verschwinden - siehe App.js renameBookmarksCategory().
        if (onRenameCategory) {
          await onRenameCategory(oldName, newName);
        }
      } else {
        setError(t('categories.existsError') || 'Kategorie existiert bereits');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEditing = () => {
    setEditingCategory(null);
    setEditingName('');
    setError('');
  };

  return (
    <div className="dynamic-panel category-panel-wrapper">
      {/* QuickShoppingList-Style Panel */}
      <div className="category-panel">
        {/* Unified Search & Add Section - ein Feld für Suche UND Hinzufügen (wie in der Lesezeichenverwaltung):
            Treffer vorhanden -> "×" zum Löschen der Suche; keine Treffer -> "+" um den eingegebenen
            Namen direkt als neue Kategorie anzulegen. */}
        <div className="unified-search-section">
          <input
            ref={inputRef}
            type="text"
            className="unified-search-input"
            placeholder={t('categories.searchPlaceholder') || 'Suchen oder neue Kategorie eingeben...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && searchTerm.trim() && filteredCategories.length === 0) {
                handleAddCategory();
              }
            }}
            autoFocus
          />
          {searchTerm.trim() && (
            filteredCategories.length > 0 ? (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title={t('clearSearch') || 'Suche löschen'}
              >
                ×
              </button>
            ) : (
              <button
                className="search-add-btn"
                onClick={handleAddCategory}
                title={t('categories.add') || 'Kategorie hinzufügen'}
              >
                +
              </button>
            )
          )}
        </div>

        {error && (
          <div className="category-error">
            ⚠️ {error}
          </div>
        )}

        {/* Header with count */}
        <div className="category-list-header">
          <span className="category-list-icon">🗂️</span>
          <span className="category-list-title">{t('categories.title') || 'Kategorien'}</span>
          <span className="category-list-count">{filteredCategories.length}</span>
        </div>

        {/* Scrollable Categories List */}
        <div className="category-list-content">
          {categories.length === 0 ? (
            <div className="category-empty">
              <p>📭 {t('categories.empty') || 'Noch keine Kategorien'}</p>
              <p className="category-empty-hint">{t('categories.emptyHint') || 'Tippe oben einen Namen ein und tippe auf + um deine erste Kategorie anzulegen!'}</p>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="category-empty">
              <p>🔍 {t('categories.noResults') || 'Keine Kategorien gefunden'}</p>
              <p className="category-empty-hint">{t('categories.noResultsHint') || 'Tippe oben auf + um sie als neue Kategorie anzulegen'}</p>
            </div>
          ) : (
            <ul className="category-items">
              {filteredCategories.map(category => (
                <li key={category} className="category-item">
                  <div className="category-item-content">
                    {editingCategory === category ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') cancelEditing();
                        }}
                        onBlur={saveEdit}
                        className="category-edit-input"
                        autoFocus
                        disabled={isSaving}
                      />
                    ) : (
                      <>
                        <span
                          className="category-item-name"
                          onDoubleClick={() => startEditing(category)}
                        >
                          📁 {stripCategoryPrefix(category)}
                        </span>
                        <span className="category-item-count">
                          {categoryStats[category] || 0} {t('bookmarks') || 'Lesezeichen'}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="category-item-actions">
                    {editingCategory === category ? (
                      <>
                        <button className="category-action-btn save" onClick={saveEdit} title={t('save') || 'Speichern'} disabled={isSaving}>
                          {isSaving ? '⏳' : '✓'}
                        </button>
                        <button className="category-action-btn cancel" onClick={cancelEditing} title={t('cancel') || 'Abbrechen'} disabled={isSaving}>✕</button>
                      </>
                    ) : (
                      <>
                        <button className="category-action-btn edit" onClick={() => startEditing(category)} title={t('edit') || 'Bearbeiten'}>✏️</button>
                        <button className="category-action-btn delete" onClick={() => handleDeleteCategory(category)} title={t('delete') || 'Löschen'}>🗑️</button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryManagement;